import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useStateRef, useLatestState, usePersistentCallback } from './hooks';
const RenderQueueContext = createContext(undefined);
const getResolvedPromise = () => Promise.resolve();
export function RenderQueueProvider({ children, enabled: _enabled }) {
    const [promiseRef, setPromiseRef] = useStateRef(getResolvedPromise());
    const [enabled, setEnabled] = useLatestState(_enabled);
    const resolvedKeysRef = useRef(new Set());
    const value = useMemo(() => ({ promiseRef, enabled, setEnabled, setPromiseRef, resolvedKeysRef }), [promiseRef, enabled, setEnabled, setPromiseRef]);
    useEffect(() => {
        // reset queue when it is disabled
        if (!enabled) {
            setPromiseRef(getResolvedPromise());
        }
    }, [enabled, setPromiseRef]);
    return _jsx(RenderQueueContext.Provider, { value: value, children: children });
}
export function useRenderQueue(key) {
    const context = useContext(RenderQueueContext);
    if (!context) {
        throw new Error('useRenderQueue must be used within a RenderQueueProvider');
    }
    const { promiseRef, enabled, setPromiseRef, resolvedKeysRef } = context;
    const [canRenderRef, setCanRenderRef] = useStateRef(!enabled);
    const resolveCb = useRef(() => { });
    const scheduleNextRender = usePersistentCallback(() => {
        resolveCb.current();
    });
    useEffect(() => {
        if (!enabled && !resolvedKeysRef.current.has(key)) {
            setCanRenderRef(true);
        }
        else {
            setPromiseRef(promiseRef.current.then(() => {
                setCanRenderRef(true);
                resolvedKeysRef.current.add(key);
                return new Promise((resolve) => {
                    resolveCb.current = resolve;
                });
            }));
        }
    }, [enabled, promiseRef, setPromiseRef, setCanRenderRef, resolvedKeysRef, key]);
    return useMemo(() => {
        return {
            canRenderRef,
            scheduleNextRender
        };
    }, [canRenderRef, scheduleNextRender]);
}
